import type {
  StudyReport,
  StudyReportConvergencePoint,
  StudyReportRunConfiguration,
} from "../api/studyReport";

type StudyReportPanelProps = {
  report: StudyReport | null;
};

function formatNumber(value: unknown, digits = 4): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const mag = Math.abs(value);
  if (mag !== 0 && mag < 0.01) return value.toExponential(2);
  return value.toPrecision(digits);
}

function formatPct(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

function formatRelativeDelta(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const pct = value * 100;
  const places = Math.abs(pct) > 0 && Math.abs(pct) < 1 ? 3 : 1;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(places)}%`;
}

function formatMoney(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

function runLabel(runId: number | null | undefined, report: StudyReport): string {
  if (runId == null) return "—";
  const cfg = report.run_configurations.find((r) => r.run_id === runId);
  return cfg ? `R${cfg.run_number}` : `#${runId}`;
}

function describeOverlay(raw: unknown): string {
  if (!raw || typeof raw !== "object") return "baseline";
  const corners = (raw as { corners?: unknown }).corners;
  if (!corners || typeof corners !== "object") return "baseline";
  const parts: string[] = [];
  for (const key of ["lf", "rf", "lr", "rr"] as const) {
    const body = (corners as Record<string, unknown>)[key];
    if (!body || typeof body !== "object") continue;
    const clicks = (body as { clicks?: unknown }).clicks;
    if (Array.isArray(clicks)) {
      parts.push(`${key.toUpperCase()} [${clicks.join(", ")}]`);
    } else {
      parts.push(key.toUpperCase());
    }
  }
  return parts.length > 0 ? parts.join(" · ") : "edited overlay";
}

function scoreDirectionLabel(direction: unknown): string {
  if (direction === "min") return "lower better";
  if (direction === "max") return "higher better";
  return "target";
}

function isBaselineRelative(report: StudyReport): boolean {
  return report.objective?.baseline_relative === true;
}

function formatScore(report: StudyReport, value: unknown, digits = 4): string {
  return isBaselineRelative(report) ? formatRelativeDelta(value) : formatNumber(value, digits);
}

function formatImprovement(report: StudyReport): string {
  if (isBaselineRelative(report)) return formatRelativeDelta(report.summary.improvement);
  return formatPct(report.summary.improvement_pct);
}

function domainFor(points: StudyReportConvergencePoint[], baseline: unknown) {
  const values = points.flatMap((p) => [p.score, p.best_score])
    .filter((v): v is number => typeof v === "number" && Number.isFinite(v));
  if (typeof baseline === "number" && Number.isFinite(baseline)) {
    values.push(baseline);
  }
  if (values.length === 0) return null;
  let min = Math.min(...values);
  let max = Math.max(...values);
  if (min === max) {
    min -= Math.abs(min || 1) * 0.1;
    max += Math.abs(max || 1) * 0.1;
  }
  const pad = (max - min) * 0.12;
  return { min: min - pad, max: max + pad };
}

function StudyReportConvergenceChart({ report }: { report: StudyReport }) {
  const points = report.convergence;
  const domain = domainFor(points, report.summary.baseline_score);
  if (points.length === 0 || domain === null) {
    return (
      <div className="study-report-empty" data-testid="study-report-chart-empty">
        No scored candidate runs yet.
      </div>
    );
  }

  const width = 900;
  const height = 280;
  const pad = { left: 58, right: 22, top: 20, bottom: 34 };
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const xFor = (i: number) => {
    if (points.length === 1) return pad.left + plotW * 0.5;
    return pad.left + (i / (points.length - 1)) * plotW;
  };
  const yFor = (value: number) => (
    pad.top + ((domain.max - value) / (domain.max - domain.min)) * plotH
  );
  const baseline =
    typeof report.summary.baseline_score === "number"
      ? report.summary.baseline_score
      : null;
  const bestSegments = points
    .map((p, i) => ({ x: xFor(i), y: p.best_score == null ? null : yFor(p.best_score) }))
    .filter((p): p is { x: number; y: number } => p.y !== null);
  const bestPath = bestSegments.reduce((d, p, i) => {
    if (i === 0) return `M ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
    return `${d} H ${p.x.toFixed(2)} V ${p.y.toFixed(2)}`;
  }, "");
  const ticks = [domain.min, (domain.min + domain.max) / 2, domain.max];

  return (
    <svg
      className="study-report-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Study objective convergence"
      data-testid="study-report-chart"
    >
      <rect x="0" y="0" width={width} height={height} rx="6" />
      {ticks.map((t) => {
        const y = yFor(t);
        return (
          <g key={t}>
            <line className="study-report-grid" x1={pad.left} y1={y} x2={width - pad.right} y2={y} />
            <text className="study-report-axis" x={pad.left - 10} y={y + 4} textAnchor="end">
              {formatScore(report, t, 3)}
            </text>
          </g>
        );
      })}
      {baseline !== null && (
        <g>
          <line
            className="study-report-baseline"
            x1={pad.left}
            y1={yFor(baseline)}
            x2={width - pad.right}
            y2={yFor(baseline)}
          />
          <text
            className="study-report-baseline-label"
            x={width - pad.right}
            y={yFor(baseline) - 6}
            textAnchor="end"
          >
            baseline {formatScore(report, baseline, 3)}
          </text>
        </g>
      )}
      {bestPath && <path className="study-report-best-line" d={bestPath} />}
      {points.map((p, i) => {
        const x = xFor(i);
        const y = p.score == null ? null : yFor(p.score);
        if (y === null) return null;
        return (
          <g key={p.run_id}>
            <circle
              className={p.improved ? "study-report-point improved" : "study-report-point"}
              cx={x}
              cy={y}
              r={p.improved ? 5.5 : 4.5}
            >
              <title>{`R${p.run_number}: ${formatScore(report, p.score)}${p.improved ? " improved" : ""}`}</title>
            </circle>
            <text className="study-report-x" x={x} y={height - 12} textAnchor="middle">
              R{p.run_number}
            </text>
          </g>
        );
      })}
      <text className="study-report-axis-title" x={pad.left} y={height - 12}>
        candidate run
      </text>
      <text
        className="study-report-axis-title"
        x={16}
        y={pad.top}
        transform={`rotate(-90 16 ${pad.top})`}
      >
        {isBaselineRelative(report) ? "relative change" : "objective score"}
      </text>
    </svg>
  );
}

function ReportConfigurationRow({
  cfg,
  report,
}: {
  cfg: StudyReportRunConfiguration;
  report: StudyReport;
}) {
  return (
    <tr>
      <td>R{cfg.run_number}</td>
      <td>{cfg.proposal_index == null ? "Reference" : `Proposal ${cfg.proposal_index + 1}`}</td>
      <td>{cfg.status}</td>
      <td className="num">{formatScore(report, cfg.objective_score)}</td>
      <td>{describeOverlay(cfg.damper_setting)}</td>
      <td>{cfg.reasoning_summary ?? "—"}</td>
    </tr>
  );
}

type StudyConclusion = {
  headline: string;
  what_happened: string;
  what_worked: string;
  recommendation: string;
  caveats: string;
};

/** Narrow the open ``summary.conclusion`` blob (LLM end-of-loop review) into a
 * renderable shape. Returns null unless at least the headline is present. */
function readConclusion(report: StudyReport): StudyConclusion | null {
  const raw = (report.summary as Record<string, unknown>).conclusion;
  if (raw === null || typeof raw !== "object") return null;
  const c = raw as Record<string, unknown>;
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  const headline = str(c.headline);
  if (!headline) return null;
  return {
    headline,
    what_happened: str(c.what_happened),
    what_worked: str(c.what_worked),
    recommendation: str(c.recommendation),
    caveats: str(c.caveats),
  };
}

export function StudyReportPanel({ report }: StudyReportPanelProps) {
  if (report === null) return null;
  const noSession = report.status === "no_session";
  const steps = report.steps.slice(-10);
  const conclusion = noSession ? null : readConclusion(report);

  return (
    <section className="card study-report" data-testid="study-report-panel">
      <div className="study-report-head">
        <div>
          <p className="eyebrow">Study report</p>
          <h4>
            {noSession
              ? "No optimisation session yet"
              : `${report.session?.generator === "claude" ? "Claude" : "AutoResearch"} run summary`}
          </h4>
        </div>
        <span className={`study-report-state is-${report.status}`}>
          {report.status.replace("_", " ")}
        </span>
      </div>

      {noSession ? (
        <p className="muted">Run AutoResearch to populate the report.</p>
      ) : (
        <>
          <div className="study-report-summary" data-testid="study-report-summary">
            <div>
              <span>Objective</span>
              <strong>{report.objective?.display_name ?? report.objective?.objective_id ?? "—"}</strong>
              <small>
                {scoreDirectionLabel(report.objective?.direction)}
                {isBaselineRelative(report) ? " · relative to R0" : ""}
              </small>
            </div>
            <div>
              <span>Best</span>
              <strong>{runLabel(report.summary.best_run_id as number | null, report)}</strong>
              <small className="num">{formatScore(report, report.summary.best_score)}</small>
            </div>
            <div>
              <span>Improvement</span>
              <strong className="num">{formatImprovement(report)}</strong>
              <small className="num">
                {isBaselineRelative(report)
                  ? "vs R0"
                  : formatNumber(report.summary.improvement)}
              </small>
            </div>
            <div>
              <span>Runs</span>
              <strong>
                {String(report.summary.completed_runs ?? 0)}/{String(report.summary.candidate_runs ?? 0)}
              </strong>
              <small>complete</small>
            </div>
            <div>
              <span>Claude spend</span>
              <strong className="num">{formatMoney(report.session?.estimated_cost_usd)}</strong>
              <small>{report.session?.model ?? "—"}</small>
            </div>
          </div>

          {conclusion && (
            <div className="study-conclusion" data-testid="study-conclusion">
              <p className="study-conclusion-eyebrow">Study conclusion</p>
              <p className="study-conclusion-headline">{conclusion.headline}</p>
              <dl className="study-conclusion-fields">
                {conclusion.what_happened && (
                  <>
                    <dt>What happened</dt>
                    <dd>{conclusion.what_happened}</dd>
                  </>
                )}
                {conclusion.what_worked && (
                  <>
                    <dt>What worked</dt>
                    <dd>{conclusion.what_worked}</dd>
                  </>
                )}
                {conclusion.recommendation && (
                  <>
                    <dt>Recommendation</dt>
                    <dd>{conclusion.recommendation}</dd>
                  </>
                )}
                {conclusion.caveats && (
                  <>
                    <dt>Caveats</dt>
                    <dd>{conclusion.caveats}</dd>
                  </>
                )}
              </dl>
            </div>
          )}

          <StudyReportConvergenceChart report={report} />

          <div className="study-report-grid-block">
            <div className="study-report-steps">
              <h5>Steps</h5>
              {steps.length === 0 ? (
                <p className="muted">No event log entries recorded.</p>
              ) : (
                <ol>
                  {steps.map((step) => (
                    <li key={step.id}>
                      <strong>{step.title}</strong>
                      {step.detail && <span>{step.detail}</span>}
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="study-report-configs">
              <h5>Run configurations</h5>
              <div className="study-report-table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Run</th>
                      <th>Step</th>
                      <th>Status</th>
                      <th>Score</th>
                      <th>Damper overlay</th>
                      <th>Claude note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.run_configurations.map((cfg) => (
                      <ReportConfigurationRow key={cfg.run_id} cfg={cfg} report={report} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
